import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { ClarityModule, ClrForm } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { firstValueFrom } from 'rxjs';
import { Table } from 'src/app/models/ui/table';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ZählerEinheit } from '../zaehler.types';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { ZaehlerApiService } from 'src/app/services/APIService/zaehler.api.service';
import { ConfirmService } from 'src/app/services/confirm.service';

@Component({
  selector: 'app-zaehler-einheiten',
  templateUrl: './zaehler-einheiten.component.html',
  styleUrls: ['./zaehler-einheiten.component.scss'],
  imports: [ClarityModule, FormsModule, MrTranslatePipe, HideIDColumnsPipe, TablePrettyPrintPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZaehlerEinheitenComponent {
  readonly modalOpen = model<boolean>(true);
  readonly otypid = input.required<number>();
  readonly refresh = output<void>();

  private readonly api = inject(ZaehlerApiService);
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly confirmService = inject(ConfirmService);

  private readonly clrForm = viewChild(ClrForm);
  private readonly ngForm = viewChild(NgForm);

  protected readonly mehResource = this.api.getZaehlerWerteEinheiten(this.otypid);

  protected readonly meh = computed(() => this.mehResource.value());

  protected readonly selectedRow = signal<ZählerEinheit>(undefined);

  constructor() {
    effect(() => {
      const selected = this.selectedRow();
      untracked(() => this.ngForm().resetForm(selected));
    });
  }

  protected async saveIfValid() {
    const ngForm = this.ngForm();
    if (!ngForm.valid) {
      this.clrForm().markAsTouched();
      return;
    }

    const { success, error, ID } = await this.api.setZaehlerWerteEinheit(this.otypid(), {
      ID: this.selectedRow()?.ID,
      ...ngForm.value,
    });

    if (success) {
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
      this.mehResource.reload();
      this.refresh.emit();
      this.selectedRow.set({ ID } as ZählerEinheit);
    } else this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
  }

  protected async deleteRow() {
    const ID = this.selectedRow()?.ID;
    if (!ID) return;
    const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie diese Zeile löschen wollen?');
    const ok = await this.confirmService.danger(text);
    if (!ok) return;

    const { success, error } = await this.api.deleteZahlerItem('meh', ID);
    if (success) {
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.selectedRow.set(null);
      this.mehResource.reload();
      this.refresh.emit();
    } else this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
  }
}
